import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DriverShell from "../components/DriverShell";
import { authenticatedApi } from "../lib/api";
import { getDriverSession } from "../lib/driverSession";

const FOOD_QUEUE_CACHE = "dh_driver_food_queue_v2";
const SERVICE_QUEUE_CACHE = "dh_driver_service_queue_v1";
const ASSIGNED_FOOD_CACHE = "dh_driver_assigned_food_v1";

function readAssignedFood() {
  try {
    return JSON.parse(localStorage.getItem(ASSIGNED_FOOD_CACHE) || "[]").map(Number).filter(Number.isFinite);
  } catch {
    return [];
  }
}

function readFoodQueueCache(driverId) {
  try {
    const cached = JSON.parse(localStorage.getItem(FOOD_QUEUE_CACHE) || "null");
    return cached?.driverId === driverId && cached?.payload ? cached.payload : null;
  } catch {
    return null;
  }
}

function readServiceQueueCache(driverId) {
  try {
    const cached = JSON.parse(localStorage.getItem(SERVICE_QUEUE_CACHE) || "null");
    return cached?.driverId === driverId && cached?.payload ? cached.payload : null;
  } catch {
    return null;
  }
}

function normalizeQueue(payload) {
  return {
    ...payload,
    new_orders: Array.isArray(payload?.new_orders) ? payload.new_orders : [],
    accepted_orders: Array.isArray(payload?.accepted_orders) ? payload.accepted_orders : [],
    pickedup_orders: Array.isArray(payload?.pickedup_orders) ? payload.pickedup_orders : [],
  };
}

function ServiceCard({ entry, onTake, taking }) {
  const isFood = entry.kind === "food";
  const order = entry.order;
  const label = isFood
    ? ((order.flow_v1?.waiting_store_acceptance || (entry.stage === "active" && Number(order.orderstatus_id) === 1))
      ? "RESERVADO · ESPERANDO LOCAL"
      : (Number(order.orderstatus_id) === 1 ? "NUEVO · LOCAL PENDIENTE" : "COMIDA"))
    : order.order_type_label || order.service_type;
  const reference = isFood ? order.unique_order_id : order.reference;
  const title = isFood ? order.restaurant?.name : order.pickup_address;
  const destination = isFood ? order.address : order.dropoff_address;
  const limitReached = entry.stage === "new" && order.can_accept_delivery === false;
  const action = entry.stage === "new"
    ? (limitReached ? "Límite alcanzado" : "Tomar pedido")
    : entry.stage === "pickedup" ? "Continuar entrega" : "Ver pedido";
  const detailUrl = `/delivery/orders/${encodeURIComponent(reference)}?type=${entry.kind}`;
  return (
    <article className="driver-order-card">
      <Link className="driver-order-card__main" to={detailUrl}>
        <div className="driver-order-card__meta">
          <span className={`driver-order-card__tag is-${entry.kind}`}>{label}</span>
          <small>#{reference}</small>
        </div>
        <h2>{title || "Servicio Delivery Huaraz"}</h2>
        <p>⌖ {destination || "Dirección por confirmar"}</p>
      </Link>
      {entry.stage === "new" ? (
        <button className="driver-order-card__action" disabled={taking || limitReached} onClick={() => onTake(entry)} type="button">
          {taking ? "Tomando…" : action} <span aria-hidden="true">›</span>
        </button>
      ) : (
        <Link className="driver-order-card__action" to={detailUrl}>{action} <span aria-hidden="true">›</span></Link>
      )}
    </article>
  );
}

export default function DriverOrdersPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const driver = getDriverSession();
  const canTaxi = driver?.services?.taxi === true;
  const canDeliveryServices = driver?.services?.moto !== false;
  const enabledServiceTypes = [
    ...(canDeliveryServices ? ["DELIVERY"] : []),
    ...(canTaxi ? ["TAXI"] : []),
  ];
  const [food, setFood] = useState(() => readFoodQueueCache(driver?.id));
  const [services, setServices] = useState(() => readServiceQueueCache(driver?.id));
  const [filter, setFilter] = useState("all");
  const requestedStage = searchParams.get("tab");
  const stageFilter = ["active", "pickedup"].includes(requestedStage) ? requestedStage : "new";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [takingId, setTakingId] = useState(null);
  const [assignedFoodIds, setAssignedFoodIds] = useState(readAssignedFood);
  const loadInFlight = useRef(false);
  const hasLoadedData = useRef(Boolean(food));

  async function load() {
    if (!driver?.auth_token || loadInFlight.current) return;
    loadInFlight.current = true;
    try {
      // Food orders are the operational priority. Load them first so an
      // auxiliary service request can never delay or hide the restaurant
      // queue on a constrained hosting connection.
      const foodRequest = authenticatedApi("/delivery/get-delivery-orders", driver.auth_token, {
        method: "POST", body: "{}", retry: 2, timeoutMs: 25000,
      }).then((value) => {
        const queue = normalizeQueue(value);
        setFood(queue);
        localStorage.setItem(FOOD_QUEUE_CACHE, JSON.stringify({
          driverId: driver.id,
          savedAt: Date.now(),
          payload: queue,
        }));
        hasLoadedData.current = true;
        setLoading(false);
        setError("");
        return queue;
      });

      const foodResult = await Promise.resolve(foodRequest)
        .then((value) => ({ status: "fulfilled", value }))
        .catch((reason) => ({ status: "rejected", reason }));

      const serviceRequest = enabledServiceTypes.length
        ? authenticatedApi("/delivery/service-orders", driver.auth_token, {
          method: "POST",
          body: JSON.stringify({ service_types: enabledServiceTypes }),
          retry: 0,
          timeoutMs: 10000,
        }).then((value) => {
          const queue = normalizeQueue(value);
          setServices(queue);
          localStorage.setItem(SERVICE_QUEUE_CACHE, JSON.stringify({
            driverId: driver.id,
            savedAt: Date.now(),
            payload: queue,
          }));
          hasLoadedData.current = true;
          return queue;
        })
        : Promise.resolve(normalizeQueue({}));
      await Promise.resolve(serviceRequest)
        .then((value) => ({ status: "fulfilled", value }))
        .catch((reason) => ({ status: "rejected", reason }));
      if (foodResult.status === "fulfilled") {
        hasLoadedData.current = true;
        setError("");
      } else {
        // Si existe una cola recuperada o una asignación confirmada, no cubrir
        // la pantalla con un error grande. El sondeo continúa en segundo plano.
        setError(hasLoadedData.current ? "" : "Conexión inestable. Seguimos intentando cargar tus pedidos.");
      }
    } finally {
      loadInFlight.current = false;
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 15000);
    const refresh = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [driver?.auth_token]);

  function moveToAccepted(entry) {
    const order = entry.order;
    if (entry.kind === "food") {
      setAssignedFoodIds((current) => {
        const next = Array.from(new Set([...current, Number(order.id)]));
        localStorage.setItem(ASSIGNED_FOOD_CACHE, JSON.stringify(next));
        return next;
      });
      setFood((current) => {
        const next = normalizeQueue(current || {});
        const accepted = { ...order, delivery_assigned_to_me: true };
        const payload = {
          ...next,
          new_orders: next.new_orders.filter((item) => Number(item.id) !== Number(order.id)),
          accepted_orders: [accepted, ...next.accepted_orders.filter((item) => Number(item.id) !== Number(order.id))],
        };
        localStorage.setItem(FOOD_QUEUE_CACHE, JSON.stringify({ driverId: driver.id, savedAt: Date.now(), payload }));
        return payload;
      });
    } else {
      setServices((current) => {
        const next = normalizeQueue(current || {});
        return {
          ...next,
          new_orders: next.new_orders.filter((item) => Number(item.id) !== Number(order.id)),
          accepted_orders: [{ ...order, status: "ASSIGNED" }, ...next.accepted_orders.filter((item) => Number(item.id) !== Number(order.id))],
        };
      });
    }
  }

  function removeUnavailable(entry) {
    const order = entry.order;
    if (entry.kind === "food") {
      setFood((current) => {
        const next = normalizeQueue(current || {});
        return { ...next, new_orders: next.new_orders.filter((item) => Number(item.id) !== Number(order.id)) };
      });
    } else {
      setServices((current) => {
        const next = normalizeQueue(current || {});
        return { ...next, new_orders: next.new_orders.filter((item) => Number(item.id) !== Number(order.id)) };
      });
    }
  }

  async function takeOrder(entry) {
    if (takingId !== null) return;
    const order = entry.order;
    const name = entry.kind === "food" ? order.restaurant?.name : order.pickup_address;
    if (!window.confirm(`¿Tomar el pedido de ${name || "este servicio"}?`)) return;
    setTakingId(`${entry.kind}-${order.id}`);
    setError("");
    try {
      if (entry.kind === "food") {
        const response = await authenticatedApi("/delivery/accept-to-deliver", driver.auth_token, {
          method: "POST",
          body: JSON.stringify({ order_id: order.id }),
        });
        if (response?.already_accepted && !response?.already_accepted_by_me) {
          removeUnavailable(entry);
          throw new Error("Otra moto ya tomó este pedido.");
        }
        if (response?.max_order) throw new Error("Alcanzaste el máximo de pedidos activos permitido.");
      } else {
        await authenticatedApi(`/delivery/service-orders/${order.id}/accept`, driver.auth_token, {
          method: "POST",
          body: "{}",
        });
      }
      moveToAccepted(entry);
      navigate("/delivery/orders?tab=active", { replace: true });
      window.setTimeout(load, 700);
    } catch (requestError) {
      setError(requestError.message);
      await load();
    } finally {
      setTakingId(null);
    }
  }

  const entries = useMemo(() => {
    const rememberedFood = new Set(assignedFoodIds.map(Number));
    const result = [
      ...(food?.new_orders || []).map((order) => ({ kind: "food", stage: rememberedFood.has(Number(order.id)) ? "active" : "new", order })),
      ...(food?.accepted_orders || []).map((order) => ({ kind: "food", stage: "active", order })),
      ...(food?.pickedup_orders || []).map((order) => ({ kind: "food", stage: "pickedup", order })),
      ...(services?.new_orders || []).map((order) => ({ kind: order.service_type === "TAXI" ? "taxi" : "delivery", stage: "new", order })),
      ...(services?.accepted_orders || []).map((order) => ({ kind: order.service_type === "TAXI" ? "taxi" : "delivery", stage: "active", order })),
      ...(services?.pickedup_orders || []).map((order) => ({ kind: order.service_type === "TAXI" ? "taxi" : "delivery", stage: "pickedup", order })),
    ];
    const unique = new Map();
    result.forEach((entry) => unique.set(`${entry.kind}-${entry.order.id}`, entry));
    return Array.from(unique.values()).filter((entry) => entry.stage === stageFilter && (filter === "all" || entry.kind === filter));
  }, [filter, food, services, stageFilter, assignedFoodIds]);

  const pageTitle = stageFilter === "active" ? "Pedidos aceptados" : stageFilter === "pickedup" ? "Pedidos recogidos" : "Servicios disponibles";

  return (
    <DriverShell>
      <div className="driver-page-heading"><small>COLA DE TRABAJO</small><h1>{pageTitle}</h1><span>{entries.length}</span></div>
      <div className="driver-filters">
        {[
          ["all", "Todos"],
          ["food", "Comida"],
          ...(canDeliveryServices ? [["delivery", "Envíos"]] : []),
          ...(canTaxi ? [["taxi", "Taxi"]] : []),
        ].map(([value, label]) => (
          <button className={filter === value ? "is-active" : ""} key={value} onClick={() => setFilter(value)} type="button">{label}</button>
        ))}
      </div>
      {loading && <div className="food-message"><b>Actualizando servicios…</b></div>}
      {error && <div className="food-message food-message--error"><b>{error}</b></div>}
      {!loading && !entries.length && <div className="food-message"><b>No hay servicios en esta categoría.</b><span>La lista se actualiza automáticamente.</span></div>}
      <section className="driver-order-list">{entries.map((entry) => <ServiceCard entry={entry} key={`${entry.kind}-${entry.order.id}`} onTake={takeOrder} taking={takingId === `${entry.kind}-${entry.order.id}`} />)}</section>
    </DriverShell>
  );
}
